import React, { useState, useEffect, useCallback, memo } from 'react';
import { 
  Plus, Trash2, MessageSquare, Clock, Sparkles, 
  ChevronDown, ChevronUp, Eye, EyeOff, AlertCircle
} from 'lucide-react';

// Move MessageSequence outside as a memoized component
const MessageSequence = memo(({ 
  sequence, 
  darkMode,
  isExpanded,
  canDelete,
  onToggleExpanded,
  onUpdateSequence,
  onUpdateOpener,
  onAddFollowup,
  onRemoveFollowup,
  onUpdateFollowup,
  onRemoveSequence,
  onGenerateAISuggestion,
  expandedMessages,
  onToggleMessageExpanded
}) => {
  // Local state for text inputs to prevent focus loss
  const [localOpenerText, setLocalOpenerText] = useState(sequence.opener.text);
  const [localFollowupTexts, setLocalFollowupTexts] = useState({});
  
  // Initialize follow-up texts
  useEffect(() => {
    setLocalFollowupTexts(prev => {
      const texts = { ...prev };
      sequence.followups.forEach(f => {
        if (!(f.id in texts)) {
          texts[f.id] = f.text;
        }
      });
      return texts;
    });
  }, [sequence.followups.length]); // Only re-init when followups are added/removed
  
  // Sync opener text from props only when it changes externally (e.g., AI suggestion)
  useEffect(() => {
    setLocalOpenerText(sequence.opener.text);
  }, [sequence.opener.text]);
  
  return (
    <div
      className={`border rounded transition-all ${
        sequence.enabled
          ? darkMode 
            ? 'border-border bg-surface/50' 
            : 'border-gray-200 bg-white'
          : darkMode
            ? 'border-border bg-surface/50 opacity-60'
            : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      {/* Sequence Header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${
              sequence.enabled 
                ? 'bg-gradient-to-r from-accent to-accent-hover'
                : darkMode ? 'bg-muted' : 'bg-gray-400'
            }`} />
            
            <div>
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {sequence.name}
              </h4>
              <p className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                1 opener + {sequence.followups.length} follow-ups
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Enable/Disable Toggle */}
            <button
              onClick={() => onUpdateSequence(sequence.id, 'enabled', !sequence.enabled)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                sequence.enabled
                  ? darkMode 
                    ? 'bg-accent/20 text-accent' 
                    : 'bg-accent/10 text-accent'
                  : darkMode
                    ? 'bg-elevated text-muted'
                    : 'bg-gray-100 text-gray-500'
              }`}
            >
              {sequence.enabled ? 'Enabled' : 'Disabled'}
            </button>
            
            {/* Expand/Collapse */}
            <button
              onClick={() => onToggleExpanded(sequence.id)}
              className={`p-1.5 rounded transition-colors ${
                darkMode 
                  ? 'text-secondary hover:text-primary hover:bg-elevated' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {/* Delete */}
            {canDelete && (
              <button
                onClick={() => onRemoveSequence(sequence.id)}
                className={`p-1.5 rounded transition-colors ${
                  darkMode 
                    ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                    : 'text-red-500 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-700/50">
          <div className="p-4 space-y-4">
            {/* Opener */}
            <div className={`p-4 rounded ${darkMode ? 'bg-surface/50' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <label className={`text-sm font-semibold flex items-center space-x-2 ${darkMode ? 'text-accent' : 'text-accent'}`}>
                  <MessageSquare className="w-4 h-4" />
                  <span>Opening Message</span>
                </label>
                <button
                  onClick={async () => {
                    const suggestion = await onGenerateAISuggestion('opener');
                    setLocalOpenerText(suggestion); // Update local state
                    onUpdateOpener(sequence.id, 'text', suggestion); // Also update parent
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors flex items-center space-x-1 ${
                    darkMode 
                      ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                >
                  <Sparkles className="w-3 h-3" />
                  <span>AI Suggest</span>
                </button>
              </div>
              <textarea
                id={`opener-${sequence.id}`}
                name={`opener-${sequence.id}`}
                value={localOpenerText}
                onChange={(e) => setLocalOpenerText(e.target.value)}
                onBlur={(e) => onUpdateOpener(sequence.id, 'text', e.target.value)}
                placeholder="Enter your opening message... Use {firstName}, {company}, etc. for personalization"
                rows={3}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent text-sm ${
                  darkMode 
                    ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                }`}
              />
              <div className="mt-2 flex items-center justify-between">
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                  {localOpenerText.length} characters
                </span>
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                  Variables: {'{firstName}'}, {'{company}'}, {'{industry}'}
                </span>
              </div>
            </div>
            
            {/* Follow-ups */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>
                  Follow-up Messages
                </label>
              </div>
              
              {sequence.followups.map((followup, fIndex) => {
                const isFollowupExpanded = expandedMessages.has(followup.id);
                
                return (
                  <div
                    key={followup.id}
                    className={`border rounded ${
                      followup.enabled
                        ? darkMode ? 'border-border bg-surface/50' : 'border-gray-200 bg-white'
                        : darkMode ? 'border-border bg-surface/30' : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-2 h-2 rounded-full ${
                            followup.enabled ? 'bg-emerald-500' : darkMode ? 'bg-muted' : 'bg-gray-400'
                          }`} />
                          <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Follow-up {fIndex + 1}
                          </span>
                          <div className="flex items-center space-x-2">
                            <Clock className={`w-3 h-3 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`} />
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={followup.delay_days}
                              onChange={(e) => onUpdateFollowup(sequence.id, followup.id, 'delay_days', parseInt(e.target.value))}
                              className={`w-12 px-2 py-0.5 text-xs rounded border ${
                                darkMode 
                                  ? 'bg-surface border-border text-primary' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                            <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                              days after opener
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => onUpdateFollowup(sequence.id, followup.id, 'enabled', !followup.enabled)}
                            className={`p-1 rounded transition-colors ${
                              followup.enabled
                                ? darkMode ? 'text-emerald-400' : 'text-green-600'
                                : darkMode ? 'text-slate-500' : 'text-gray-400'
                            }`}
                          >
                            {followup.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          </button>
                          
                          <button
                            onClick={() => onToggleMessageExpanded(followup.id)}
                            className={`p-1 rounded transition-colors ${
                              darkMode 
                                ? 'text-slate-400 hover:text-slate-200' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            {isFollowupExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                          
                          <button
                            onClick={() => onRemoveFollowup(sequence.id, followup.id)}
                            className={`p-1 rounded transition-colors ${
                              darkMode 
                                ? 'text-red-400 hover:text-red-300' 
                                : 'text-red-500 hover:text-red-600'
                            }`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      
                      {isFollowupExpanded && (
                        <div className="mt-3">
                          <textarea
                            id={`followup-${followup.id}`}
                            name={`followup-${followup.id}`}
                            value={localFollowupTexts[followup.id] || ''}
                            onChange={(e) => setLocalFollowupTexts(prev => ({ ...prev, [followup.id]: e.target.value }))}
                            onBlur={(e) => onUpdateFollowup(sequence.id, followup.id, 'text', e.target.value)}
                            placeholder="Enter your follow-up message..."
                            rows={3}
                            className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent text-sm ${
                              darkMode 
                                ? 'bg-surface border-border text-primary placeholder-secondary focus:ring-accent' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                            }`}
                          />
                          <div className="mt-1">
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                              {(localFollowupTexts[followup.id] || '').length} characters
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <button
                onClick={() => onAddFollowup(sequence.id)}
                className={`w-full py-2 border border-dashed rounded transition-colors text-sm flex items-center justify-center space-x-2 ${
                  darkMode 
                    ? 'border-slate-700 text-slate-400 hover:border-emerald-500 hover:text-emerald-400'
                    : 'border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span>Add Follow-up</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

MessageSequence.displayName = 'MessageSequence';

export const MessageSequencesFull = ({ data, onDataChange, darkMode }) => {
  const [activeSequenceIndex, setActiveSequenceIndex] = useState(0);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [previewMode, setPreviewMode] = useState(false);
  
  // Initialize sequences structure if needed - only run once on mount
  useEffect(() => {
    if (!data.messageSequences || !Array.isArray(data.messageSequences)) {
      // Convert from legacy structure if it exists
      let initialSequences = [];
      
      // Check if we have legacy structure
      if (data.messages && typeof data.messages === 'object') {
        // Convert legacy openers to sequences
        if (data.messages.openers && Array.isArray(data.messages.openers)) {
          data.messages.openers.forEach((opener, index) => {
            const sequence = {
              id: `sequence_${index}`,
              name: `Sequence ${String.fromCharCode(65 + index)}`,
              enabled: opener.enabled !== false,
              opener: {
                id: `opener_${index}`,
                text: opener.text || '',
                enabled: true
              },
              followups: []
            };
            
            // If this is the first opener, add any existing follow-ups to it
            if (index === 0 && data.messages.followups && Array.isArray(data.messages.followups)) {
              sequence.followups = data.messages.followups.map((followup, fIndex) => ({
                id: `followup_legacy_${index}_${fIndex}`,
                text: followup.text || '',
                enabled: followup.enabled !== false,
                delay_days: followup.delay_days || (fIndex + 1) * 3
              }));
            }
            
            initialSequences.push(sequence);
          });
        } else if (data.messages.opener_a) {
          // Legacy single opener structure
          const sequence = {
            id: 'sequence_0',
            name: 'Sequence A',
            enabled: true,
            opener: {
              id: 'opener_0',
              text: data.messages.opener_a.text || '',
              enabled: true
            },
            followups: []
          };
          
          // Add legacy follow-ups
          ['followup1', 'followup2', 'followup3'].forEach((key, index) => {
            if (data.messages[key] && data.messages[key].text) {
              sequence.followups.push({
                id: `followup_legacy_0_${index}`,
                text: data.messages[key].text || '',
                enabled: data.messages[key].enabled !== false,
                delay_days: data.messages[key].delay_days || (index + 1) * 3
              });
            }
          });
          
          initialSequences.push(sequence);
        }
      }
      
      // If no sequences at all, create default
      if (initialSequences.length === 0) {
        initialSequences.push({
          id: 'sequence_0',
          name: 'Sequence A',
          enabled: true,
          opener: {
            id: 'opener_0',
            text: '',
            enabled: true
          },
          followups: []
        });
      }
      
      onDataChange({
        ...data,
        messageSequences: initialSequences,
        messageSettings: {
          useAIOptimization: data.messageSettings?.useAIOptimization || false
        }
      });
    }
  }, []);


  const toggleMessageExpanded = (messageId) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  const addSequence = () => {
    const sequences = [...data.messageSequences];
    const newId = `sequence_${sequences.length}`;
    
    sequences.push({
      id: newId,
      name: `Sequence ${String.fromCharCode(65 + sequences.length)}`,
      enabled: true,
      opener: {
        id: `opener_${sequences.length}`,
        text: '',
        enabled: true
      },
      followups: []
    });
    
    onDataChange({ ...data, messageSequences: sequences });
    setActiveSequenceIndex(sequences.length - 1); // Switch to the new tab
  };

  const removeSequence = (sequenceId) => {
    let sequences = data.messageSequences.filter(s => s.id !== sequenceId);
    
    // Rename remaining sequences
    sequences = sequences.map((seq, index) => ({
      ...seq,
      name: `Sequence ${String.fromCharCode(65 + index)}`
    }));
    
    // Adjust active tab if needed
    if (activeSequenceIndex >= sequences.length) {
      setActiveSequenceIndex(Math.max(0, sequences.length - 1));
    }
    
    onDataChange({ ...data, messageSequences: sequences });
  };

  const updateSequence = useCallback((sequenceId, field, value) => {
    const sequences = [...data.messageSequences];
    const sequence = sequences.find(s => s.id === sequenceId);
    if (sequence) {
      sequence[field] = value;
      onDataChange({ ...data, messageSequences: sequences });
    }
  }, [data, onDataChange]);

  const updateOpener = useCallback((sequenceId, field, value) => {
    const sequences = [...data.messageSequences];
    const sequence = sequences.find(s => s.id === sequenceId);
    if (sequence && sequence.opener) {
      sequence.opener[field] = value;
      onDataChange({ ...data, messageSequences: sequences });
    }
  }, [data, onDataChange]);

  const addFollowup = (sequenceId) => {
    const sequences = [...data.messageSequences];
    const sequence = sequences.find(s => s.id === sequenceId);
    
    if (sequence) {
      const newFollowup = {
        id: `followup_new_${sequenceId}_${Date.now()}`, // Unique ID with timestamp
        text: '',
        enabled: true,
        delay_days: (sequence.followups.length + 1) * 3
      };
      
      sequence.followups.push(newFollowup);
      onDataChange({ ...data, messageSequences: sequences });
      setExpandedMessages(new Set([...expandedMessages, newFollowup.id]));
    }
  };

  const removeFollowup = (sequenceId, followupId) => {
    const sequences = [...data.messageSequences];
    const sequence = sequences.find(s => s.id === sequenceId);
    
    if (sequence) {
      sequence.followups = sequence.followups.filter(f => f.id !== followupId);
      onDataChange({ ...data, messageSequences: sequences });
    }
  };

  const updateFollowup = useCallback((sequenceId, followupId, field, value) => {
    const sequences = [...data.messageSequences];
    const sequence = sequences.find(s => s.id === sequenceId);
    
    if (sequence) {
      const followup = sequence.followups.find(f => f.id === followupId);
      if (followup) {
        followup[field] = value;
        onDataChange({ ...data, messageSequences: sequences });
      }
    }
  }, [data, onDataChange]);

  const generateAISuggestion = async (type) => {
    const suggestions = {
      opener: [
        "Hey {firstName}! Noticed you're in {industry} - we've helped similar companies increase their outreach by 300%. Interested in a quick chat?",
        "Hi {firstName}, saw your recent post about {topic}. Have you considered how automation could help scale that approach?",
        "Quick question {firstName} - are you still looking for ways to improve your Instagram engagement?"
      ],
      followup: [
        "Hey {firstName}, just following up on my previous message. Would love to show you how we've helped {similarCompany} achieve {result}.",
        "Hi again {firstName}! I know you're busy, but I think you'd find value in our 5-minute demo. When works best for you this week?",
        "{firstName}, last attempt 😊 - we're offering exclusive early access to our new features. Interested?"
      ]
    };
    
    return suggestions[type][Math.floor(Math.random() * suggestions[type].length)];
  };


  // Don't render until sequences are initialized
  if (!data.messageSequences || !Array.isArray(data.messageSequences)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${darkMode ? 'border-accent' : 'border-accent'} mx-auto mb-4`}></div>
          <p className={darkMode ? 'text-slate-400' : 'text-gray-600'}>Initializing message sequences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Message Sequences
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mt-1`}>
            Each sequence has its own opener and follow-up messages
          </p>
        </div>
        
        <button
          onClick={() => setPreviewMode(!previewMode)}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center space-x-2 ${
            previewMode
              ? darkMode 
                ? 'bg-accent/20 text-accent'
                : 'bg-accent/10 text-accent'
              : darkMode
                ? 'bg-elevated text-primary hover:bg-elevated/80'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {previewMode ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          <span>{previewMode ? 'Edit' : 'Preview'}</span>
        </button>
      </div>

      {/* Info Alert */}
      <div className={`p-4 rounded flex items-start space-x-3 ${
        darkMode ? 'bg-accent/10 border border-accent/30' : 'bg-accent/10 border border-accent/20'
      }`}>
        <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-accent' : 'text-accent'}`} />
        <div className={`text-sm ${darkMode ? 'text-accent' : 'text-accent'}`}>
          <p className="font-medium mb-1">How sequences work:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Each sequence runs independently with its own opener and follow-ups</li>
            <li>Leads are distributed evenly across enabled sequences</li>
            <li>Each lead receives messages from only one sequence</li>
          </ul>
        </div>
      </div>

      {/* Sequences or Preview */}
      {!previewMode ? (
        <div>
          {/* Sequence Tabs */}
          <div className={`flex space-x-2 mb-4 border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            {data.messageSequences.map((sequence, index) => (
              <button
                key={sequence.id}
                onClick={() => setActiveSequenceIndex(index)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeSequenceIndex === index
                    ? darkMode
                      ? 'text-accent border-accent'
                      : 'text-accent border-accent'
                    : darkMode
                      ? 'text-slate-400 hover:text-slate-200 border-transparent'
                      : 'text-gray-600 hover:text-gray-900 border-transparent'
                }`}
              >
                {sequence.name}
                {sequence.enabled && (
                  <span className={`ml-2 inline-block w-2 h-2 rounded-full ${
                    darkMode ? 'bg-accent' : 'bg-accent'
                  }`} />
                )}
              </button>
            ))}
            <button
              onClick={addSequence}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px border-transparent ${
                darkMode 
                  ? 'text-secondary hover:text-accent'
                  : 'text-gray-500 hover:text-accent'
              }`}
            >
              <Plus className="w-4 h-4 inline" />
            </button>
          </div>
          
          {/* Active Sequence Content */}
          {data.messageSequences[activeSequenceIndex] && (
            <MessageSequence 
              key={data.messageSequences[activeSequenceIndex].id} 
              sequence={data.messageSequences[activeSequenceIndex]}
              darkMode={darkMode}
              isExpanded={true}
              canDelete={data.messageSequences.length > 1}
              onToggleExpanded={() => {}}
              onUpdateSequence={updateSequence}
              onUpdateOpener={updateOpener}
              onAddFollowup={addFollowup}
              onRemoveFollowup={removeFollowup}
              onUpdateFollowup={updateFollowup}
              onRemoveSequence={removeSequence}
              onGenerateAISuggestion={generateAISuggestion}
              expandedMessages={expandedMessages}
              onToggleMessageExpanded={toggleMessageExpanded}
            />
          )}
        </div>
      ) : (
        /* Preview Mode */
        <div className={`${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} border rounded p-6`}>
          <h4 className={`font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Campaign Message Flow
          </h4>
          <div className="space-y-6">
            {data.messageSequences.filter(s => s.enabled).map((sequence) => (
              <div key={sequence.id} className={`p-4 rounded ${darkMode ? 'bg-surface' : 'bg-white'}`}>
                <h5 className={`font-medium mb-3 ${darkMode ? 'text-accent' : 'text-accent'}`}>
                  {sequence.name} ({Math.round(100 / data.messageSequences.filter(s => s.enabled).length)}% of leads)
                </h5>
                <div className="space-y-3 ml-4">
                  <div>
                    <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      Day 0 - Opening Message:
                    </span>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                      {sequence.opener.text || <em className="opacity-50">No message text yet</em>}
                    </p>
                  </div>
                  
                  {sequence.followups.filter(f => f.enabled).map((followup) => (
                    <div key={followup.id}>
                      <span className={`text-xs font-medium ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>
                        Day {followup.delay_days} - Follow-up:
                      </span>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                        {followup.text || <em className="opacity-50">No message text yet</em>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageSequencesFull;